// TenantConfigPage — route: /tenants/:tenantId
// Three tabs: Modules | Fields | Advanced Settings
// Loads TenantConfig from GET /api/super-admin/tenants/:id/config

import { useState } from "react";
import type { JSX } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { AdminNav } from "../components/AdminNav.js";
import { ModulesTab } from "../components/ModulesTab.js";
import { FieldsTab } from "../components/FieldsTab.js";
import { AdvancedTab } from "../components/AdvancedTab.js";
import { InviteAgencyAdminModal } from "../components/InviteAgencyAdminModal.js";
import { getTenantConfig } from "../lib/adminApiClient.js";

type Tab = "modules" | "fields" | "advanced";

const TABS: Array<{ key: Tab; label: string }> = [
  { key: "modules",  label: "Modules" },
  { key: "fields",   label: "Fields" },
  { key: "advanced", label: "Advanced Settings" },
];

export function TenantConfigPage(): JSX.Element {
  const { tenantId } = useParams<{ tenantId: string }>();
  const id = tenantId ?? "";
  const [tab, setTab] = useState<Tab>("modules");
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data: config, isLoading, isError } = useQuery({
    queryKey: ["tenant-config", id],
    queryFn:  () => getTenantConfig(id),
    enabled:  Boolean(id),
  });

  return (
    <div className="min-h-screen bg-slate-100">
      <AdminNav />
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600">
              <ChevronLeft className="h-4 w-4" /> Tenants
            </Link>
            <span className="text-slate-300">/</span>
            <span className="text-sm font-medium text-slate-700">Configuration</span>
            <span className="ml-1 text-xs text-slate-400 font-mono">{id}</span>
          </div>
          <button
            onClick={() => setInviteOpen(true)}
            disabled={!id}
            className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50">
            Invite Agency Admin
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 border-b border-slate-200">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === key
                  ? "border-indigo-600 text-indigo-700"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {isLoading && (
          <p className="py-10 text-center text-sm text-slate-400">Loading configuration…</p>
        )}
        {isError && (
          <p className="py-10 text-center text-sm text-red-600">Failed to load configuration.</p>
        )}
        {config && (
          <>
            {tab === "modules"  && <ModulesTab  tenantId={id} config={config} />}
            {tab === "fields"   && <FieldsTab   tenantId={id} config={config} />}
            {tab === "advanced" && <AdvancedTab tenantId={id} config={config} />}
          </>
        )}
      </div>
      {inviteOpen && (
        <InviteAgencyAdminModal tenantId={id} onClose={() => setInviteOpen(false)} />
      )}
    </div>
  );
}
